import { API_URL } from '../../../app-config/constants';
import type { UserChangePasswordRequest } from '../../../types/UserChangePasswordRequest';
import type { UserProfileDto } from '../../../types/UserDtos';
import { toFilePayload } from '../../react/features/Attachments/service';
import type { File } from '../../react/features/Attachments/types';
import { get, post, put, remove } from '../request';

const RESOURCE = `${API_URL}/users`;

getById.operation = 'READ';
// getById.byId = true; // Disabled for now. invalidates() below doesn't work when this is set
export function getById(id: string) {
    return get<UserProfileDto>(`${RESOURCE}/${id}`);
}

getByIds.operation = 'READ';
// getByIds.byIds = true; // Disabled for now. invalidates() below doesn't work when this is set
export function getByIds(ids: string[]) {
    return get<UserProfileDto[]>(`${RESOURCE}/by-ids?ids=${ids.join(',')}`);
}

updateUserWithPassword.operation = 'COMMAND';
export function updateUserWithPassword(user: UserProfileDto, password: string) {
    return put<UserProfileDto>(RESOURCE + '/' + user.id, {
        postData: user,
        headers: { 'Si-User-Password': password },
    });
}

updateUserWithoutPassword.operation = 'COMMAND';
export function updateUserWithoutPassword(user: UserProfileDto) {
    return put<UserProfileDto>(RESOURCE + '/' + user.id, { postData: user });
}

updateRoles.operation = 'COMMAND';
export function updateRoles(userId: string, roles: any) {
    return put<UserProfileDto>(`${RESOURCE}/${userId}/roles`, { postData: roles });
}

changeTwoFactorVerification.operation = 'COMMAND';
export function changeTwoFactorVerification(userId: string, required: boolean) {
    return put<UserProfileDto>(`${RESOURCE}/${userId}/2fa?required=${required.toString()}`);
}

resetTwoFactorVerification.operation = 'COMMAND';
export function resetTwoFactorVerification(userId: string) {
    return put<UserProfileDto>(`${RESOURCE}/${userId}/2fa/reset`);
}

deleteUser.operation = 'DELETE';
export function deleteUser(userId: string, thenHandlerToExecuteBeforeLaddaStateUpdate = () => {}) {
    return remove(`${RESOURCE}/${userId}`).then(thenHandlerToExecuteBeforeLaddaStateUpdate);
}

export function clearPassword(userId: string) {
    return remove(`${RESOURCE}/${userId}/password`);
}

export function changePassword(userId: string, oldPassword: string, newPassword: string) {
    const postData: UserChangePasswordRequest = { oldPassword, newPassword };
    return put<void | string>(`${RESOURCE}/${userId}/password`, { postData });
}

export function sendPasswordMail(userId: string) {
    return post<void>(`${RESOURCE}/${userId}/mail/password-reset`);
}

export function sendWelcomeMail(userId: string) {
    return post<void>(`${RESOURCE}/${userId}/mail/welcome`);
}

export function sendProfileNudge(userId: string) {
    return post<void>(`${RESOURCE}/${userId}/mail/profile-nudge`);
}

export function impersonate(userId: string) {
    return post<void>(`${RESOURCE}/${userId}/impersonate`);
}

addAttachment.operation = 'COMMAND';
export function addAttachment(userId: string, file: File, isProtected: boolean) {
    return post<UserProfileDto>(`${RESOURCE}/${userId}/attachments`, {
        postData: { ...toFilePayload(file), isProtected: isProtected },
    });
}

deleteAttachment.operation = 'COMMAND';
/**
 * @param {string} userId
 * @param {string} attachmentId
 * @returns {Promise<any>}
 */
export function deleteAttachment(userId: string, attachmentId: string) {
    return remove<UserProfileDto>(`${RESOURCE}/${userId}/attachments/${attachmentId}`);
}

updateManager.operation = 'COMMAND';
export function updateManager(userId: string, managerId: string | null) {
    if (managerId === null) {
        return put<UserProfileDto>(`${RESOURCE}/${userId}/manager`);
    }
    return put<UserProfileDto>(`${RESOURCE}/${userId}/manager?managerId=${managerId}`);
}

updateSecondaryReviewer.operation = 'COMMAND';
export function updateSecondaryReviewer(userId: string, secondaryReviewerId: string | null) {
    if (secondaryReviewerId === null) {
        return put<UserProfileDto>(`${RESOURCE}/${userId}/secondary-reviewer`);
    }
    return put<UserProfileDto>(`${RESOURCE}/${userId}/secondary-reviewer?secondaryReviewerId=${secondaryReviewerId}`);
}

invalidate.operation = 'NO_OPERATION';
invalidate.invalidates = ['getById', 'getByIds'];
export function invalidate() {
    return Promise.resolve();
}
